import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../../constants/routes.constants";
import userService from "../../services/user.service";
import { useDispatch } from "react-redux";
import { userSlice } from "../../redux/slices/user.slice";
import images from "../../assets/images";
import {
  buildCodeLoginContextFromUser,
  saveCodeLoginContext,
} from "../../utility/code-login-context";
import {
  applyGuestProfileToLogin,
  getGuestProfileLocal,
  saveGuestProfileLocal,
} from "../../utility/guest-profile";

const STEP = {
  LOADING: "loading",
  CHOICE: "choice",
  GUEST_PROFILE: "guest_profile",
  ERROR: "error",
};

const emptyGuestProfile = {
  firstname: "",
  lastname: "",
  licenseNumber: "",
};

const LoginWithTokenPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [queryParameters] = useSearchParams();
  const attempted = useRef(false);

  const [step, setStep] = useState(STEP.LOADING);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [guestProfile, setGuestProfile] = useState(emptyGuestProfile);

  const persistContext = (loginData) => {
    const context = buildCodeLoginContextFromUser(
      loginData?.user,
      loginData?.user?.code
    );
    saveCodeLoginContext(context);
    return context;
  };

  const loginWithToken = async () => {
    const token = queryParameters.get("token");
    if (!token) {
      setStep(STEP.ERROR);
      setError("Invalid or missing token.");
      return;
    }

    setIsLoading(true);
    try {
      const loginResult = await userService.LoginWithCode(undefined, token);
      if (loginResult.error === null && loginResult.data) {
        persistContext(loginResult.data);
        setPendingLogin(loginResult.data);
        setStep(STEP.CHOICE);
      } else {
        setStep(STEP.ERROR);
        setError("Invalid or expired link. Please try again.");
      }
    } catch (err) {
      console.error("Login with token error:", err);
      setStep(STEP.ERROR);
      setError("Unable to log in with this link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueAsGuest = () => {
    setError("");
    const savedProfile = getGuestProfileLocal();
    if (savedProfile) {
      setGuestProfile({
        firstname: savedProfile.firstname || "",
        lastname: savedProfile.lastname || "",
        licenseNumber: savedProfile.licenseNumber || "",
      });
    }
    setStep(STEP.GUEST_PROFILE);
  };

  const handleLoginRegister = () => {
    dispatch(userSlice.actions.logout());
    setPendingLogin(null);
    navigate(ROUTES.LOGIN_ROUTE, {
      replace: true,
      state: { fromCodeLogin: true },
    });
  };

  const handleGuestProfileSubmit = (event) => {
    event.preventDefault();
    if (!pendingLogin) {
      return;
    }

    const firstname = guestProfile.firstname?.trim();
    const lastname = guestProfile.lastname?.trim();
    const licenseNumber = guestProfile.licenseNumber?.trim();

    if (!firstname || !lastname || !licenseNumber) {
      setError("First name, last name, and licence number are required.");
      return;
    }

    setError("");
    const profile = { firstname, lastname, licenseNumber };
    saveGuestProfileLocal(profile);

    const loginWithProfile = applyGuestProfileToLogin(pendingLogin, profile);
    dispatch(userSlice.actions.login(loginWithProfile));
    persistContext(loginWithProfile);
    navigate(ROUTES.CODE_NOTIFICATION_ROUTE, { replace: true });
  };

  useEffect(() => {
    document.title = "TransferMyRx::Login with token";
  }, []);

  useEffect(() => {
    if (attempted.current) {
      return;
    }
    attempted.current = true;
    loginWithToken();
  }, [queryParameters]);

  if (step === STEP.LOADING) {
    return (
      <div className="auth-form p-4 p-sm-5 shadow-lg rounded-1 border-primary bg-white text-center">
        <div className="spinner-border text-success mb-3" />
        <div>Signing you in...</div>
      </div>
    );
  }

  if (step === STEP.ERROR) {
    return (
      <div className="auth-form p-4 p-sm-5 shadow-lg rounded-1 border-primary bg-white">
        <div className="text-center mb-3">
          <a href="https://www.TransferMyRx.com">
            <img src={images.Logo} alt="TransferMyRx" className="img-fluid" />
          </a>
        </div>
        <div className="alert alert-danger">{error}</div>
        <Link className="btn btn-default w-100" to={ROUTES.LOGIN_WITH_CODE_ROUTE}>
          Login with code
        </Link>
      </div>
    );
  }

  return (
    <div className="auth-form p-4 p-sm-5 shadow-lg rounded-1 border-primary bg-white animate__animated">
      <div className="text-center mb-3">
        <a href="https://www.TransferMyRx.com">
          <img src={images.Logo} alt="TransferMyRx" className="img-fluid" />
        </a>
      </div>

      {isLoading && (
        <div className="text-center mb-3">
          <div className="spinner-border text-success" />
        </div>
      )}

      {step === STEP.CHOICE && (
        <>
          <h5 className="text-center mb-3">How would you like to continue?</h5>
          <p className="text-center text-muted mb-4">
            You can continue as a guest for this notification, or log in /
            register with TransferMyRx.
          </p>
          {error && (
            <div className="alert alert-danger py-2" role="alert">
              {error}
            </div>
          )}
          <button
            type="button"
            className="w-100 btn btn-default btn-lg mb-3"
            disabled={isLoading}
            onClick={handleContinueAsGuest}
          >
            Continue as guest
          </button>
          <button
            type="button"
            className="w-100 btn btn-outline-secondary btn-lg"
            disabled={isLoading}
            onClick={handleLoginRegister}
          >
            Login/Register to TransferMyRx
          </button>
        </>
      )}

      {step === STEP.GUEST_PROFILE && (
        <>
          <h5 className="text-center mb-3">Your details</h5>
          <p className="text-center text-muted mb-4">
            Please enter your name and licence number to continue.
          </p>
          <form onSubmit={handleGuestProfileSubmit}>
            <div className="mb-3">
              <label className="form-label" htmlFor="guest-firstname">
                First name
              </label>
              <input
                id="guest-firstname"
                type="text"
                className="form-control"
                value={guestProfile.firstname}
                disabled={isLoading}
                onChange={(event) =>
                  setGuestProfile((prev) => ({
                    ...prev,
                    firstname: event.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="guest-lastname">
                Last name
              </label>
              <input
                id="guest-lastname"
                type="text"
                className="form-control"
                value={guestProfile.lastname}
                disabled={isLoading}
                onChange={(event) =>
                  setGuestProfile((prev) => ({
                    ...prev,
                    lastname: event.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="guest-license">
                Licence number
              </label>
              <input
                id="guest-license"
                type="text"
                className="form-control"
                value={guestProfile.licenseNumber}
                disabled={isLoading}
                onChange={(event) =>
                  setGuestProfile((prev) => ({
                    ...prev,
                    licenseNumber: event.target.value,
                  }))
                }
              />
            </div>

            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-100 btn btn-default btn-lg mb-2"
              disabled={
                isLoading ||
                !guestProfile.firstname?.trim() ||
                !guestProfile.lastname?.trim() ||
                !guestProfile.licenseNumber?.trim()
              }
            >
              Continue
            </button>
            <button
              type="button"
              className="w-100 btn btn-link"
              disabled={isLoading}
              onClick={() => {
                setError("");
                setStep(STEP.CHOICE);
              }}
            >
              Back
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default LoginWithTokenPage;
